import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";
import {
  ChevronUpIcon, ChevronDownIcon,
  GlobeAltIcon, CpuChipIcon, TagIcon, CircleStackIcon, UserGroupIcon,
  FolderIcon, BuildingOffice2Icon, DocumentPlusIcon, ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";

import { DateTime } from "luxon";
import { useDashboardUI } from "../context/GlobalContext";
import { extractUserFromToken } from "../helpers/auth";
import { userRoles } from "../helpers/constant";
import {
  formatDate,
  validateUserRole,
  buildAnalyticsQueryParams,
  resolveAnalyticsDateRange,
  isFacilityEnrolledInAgentAnalytics,
  isFacilityEnrolledInClientAppFilter,
  getDesktopBuddyLabel,
} from "../helpers/helpers";
import DashboardMetricsService from "../services/dashboardMetricsService";
import ProjectAnalytics from "../components/ui/ProjectAnalytics";
import WorkspaceAnalytics from "../components/ui/workspaceAnalytics";
import FileAnalytics from "../components/ui/FileAnalytics";
import FileGenerationAnalytics from "../components/ui/FileGenerationAnalytics";
import TokenUsageAnalytics from "../components/ui/TokenUsageAnalytics";
import UserAnalytics from "../components/ui/UserAnalytics";
import AgentAnalytics from "../components/ui/AgentAnalytics";
import QueryCategories from "../components/ui/QueryCategories";
import PlatformUsage from "../components/ui/PlatformUsage";
import GenericAnalytics from "../components/ui/genericAnalytics";
import SourceMetrics from "../components/ui/SourceMetrics";
import StatCard from "../components/ui/StatCard";

/* ============================================================
   🎨 THEME COLORS (CHANGE COLORS HERE)
   ============================================================ */
const COLORS = {
  primary: "#6366F1", // Indigo-500 (main charts)
  secondary: "#38BDF8", // Sky-400
  success: "#22C55E", // Green-500
  danger: "#EF4444", // Red-500
  textPrimary: "text-gray-800",
  textSecondary: "text-gray-500",
  cardBg: "bg-white",
  border: "border-gray-200",
};

/* ============================================================
   📊 DUMMY DATA
   ============================================================ */
const lineData = [
  { name: "Mon", value: 30 },
  { name: "Tue", value: 45 },
  { name: "Wed", value: 28 },
  { name: "Thu", value: 60 },
  { name: "Fri", value: 52 },
  { name: "Sat", value: 40 },
  { name: "Sun", value: 55 },
];

const barData = [
  { name: "PDF", count: 120 },
  { name: "DOCX", count: 90 },
  { name: "CSV", count: 70 },
  { name: "PNG", count: 50 },
];

const fileTypeTable = [
  { type: "PDF", count: 120 },
  { type: "DOCX", count: 90 },
  { type: "CSV", count: 70 },
  { type: "PNG", count: 50 },
];

/* ============================================================
   🧱 REUSABLE COMPONENTS
   ============================================================ */

const Card = ({ title, children }) => (
  <div
    className={`${COLORS.cardBg} border border-[#ECEEF4] rounded-2xl p-4`}
  >
    {title && (
      <h3 className={`text-sm font-semibold mb-3 ${COLORS.textPrimary}`}>
        {title}
      </h3>
    )}
    {children}
  </div>
);

const LineChartBlock = () => (
  <ResponsiveContainer width="100%" height={200}>
    <LineChart data={lineData}>
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip />
      <Line
        type="monotone"
        dataKey="value"
        stroke={COLORS.primary}
        strokeWidth={2}
        dot={false}
      />
    </LineChart>
  </ResponsiveContainer>
);

const BarChartBlock = () => (
  <ResponsiveContainer width="100%" height={200}>
    <BarChart data={barData}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip />
      <Bar dataKey="count" fill={COLORS.secondary} radius={[4, 4, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

// Icon tile shown beside each section title (presentation only).
const ACCORDION_ICONS = [
  { match: "Platform Insights", icon: GlobeAltIcon, tile: "bg-[#E7F7EE] text-[#12945A]" },
  { match: "Platform &", icon: GlobeAltIcon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
  { match: "Agent Analytics", icon: CpuChipIcon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
  { match: "Query Categories", icon: TagIcon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
  { match: "Token Usage", icon: CircleStackIcon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
  { match: "User Analytics", icon: UserGroupIcon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
  { match: "Projects", icon: FolderIcon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
  { match: "Workspace", icon: BuildingOffice2Icon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
  { match: "File Generation", icon: DocumentPlusIcon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
  { match: "File Upload", icon: ArrowUpTrayIcon, tile: "bg-[#F3EAFE] text-[#7C3AED]" },
];

const Accordion = ({ title, children, isOpen = true }) => {
  const [open, setOpen] = useState(isOpen);
  const meta = ACCORDION_ICONS.find((m) => String(title).startsWith(m.match));
  const SectionIcon = meta?.icon;

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center gap-3 px-4 sm:px-6 py-5 bg-transparent text-[#14122B]"
      >
        <span className="flex min-w-0 items-center gap-3">
          {SectionIcon && (
            <span className={`flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl ${meta.tile}`}>
              <SectionIcon className="h-5 w-5" />
            </span>
          )}
          <span className="min-w-0 break-words text-base sm:text-lg font-bold leading-snug tracking-tight text-left">{title}</span>
        </span>
        <span className="shrink-0 text-[#4B5266]">{open ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}</span>
      </button>
      {open && <div className="px-3 pb-4 sm:px-6 sm:pb-6">{children}</div>}
    </div>
  );
};

/* ============================================================
   🚀 MAIN COMPONENT
   ============================================================ */

export default function AnalyticsInsights() {

  const currentUser = extractUserFromToken();
  const isSuperAdmin = validateUserRole(userRoles.SUPERADMIN);

  const adminEmail = localStorage.getItem("admin_email");
  if (!adminEmail) {
    return;
  }

  const [startDate, setStartDate] = useState(() => {
    return DateTime.local().minus({ days: 7 }).startOf("day").toJSDate();
  });
  const [endDate, setEndDate] = useState(() => {
    return DateTime.local().endOf("day").toJSDate();
  });

  // Token usage analytics is needed by both the KPI header (usage_summary) and the
  // Token Usage accordion (datewise + top users). Fetch it once here and share it
  // so the endpoint is hit a single time per page load / filter change.
  const {
    selectedFacilityFilter,
    selectedStartDateFilter,
    selectedEndDateFilter,
  } = useDashboardUI();
  const [tokenUsage, setTokenUsage] = useState(null);
  const [tokenUsageLoading, setTokenUsageLoading] = useState(true);

  // The facility to scope every accordion's data to: the superadmin-adjustable
  // facility filter for superadmins, and the admin's own (stable, JWT-derived)
  // facility otherwise — admins can't change this filter. Using the JWT value
  // directly for admins, rather than waiting on the async-resolved facility
  // filter, keeps this stable across the initial render so effects below
  // don't fire twice while that filter is still resolving.
  const requestFacilityId = isSuperAdmin
    ? selectedFacilityFilter?.facility_number
    : currentUser?.facility_id;

  useEffect(() => {
    let cancelled = false;
    const fetchTokenUsage = async () => {
      try {
        setTokenUsageLoading(true);
        const queryParams = buildAnalyticsQueryParams({
          startValue: selectedStartDateFilter,
          endValue: selectedEndDateFilter,
          facilityId: requestFacilityId,
        });
        const res = await DashboardMetricsService.getTokenUsageAnalytics(queryParams);
        if (!cancelled) setTokenUsage(res.data);
      } catch (error) {
        console.error("Failed to fetch token usage analytics", error);
        if (!cancelled) setTokenUsage(null);
      } finally {
        if (!cancelled) setTokenUsageLoading(false);
      }
    };
    fetchTokenUsage();
    return () => {
      cancelled = true;
    };
  }, [selectedStartDateFilter, selectedEndDateFilter, requestFacilityId]);

  // Agent Analytics is only shown/fetched when BOTH the logged-in user's own
  // facility AND the facility whose data would actually be requested are
  // enrolled in REACT_APP_AGENT_ANALYTICS_FACILITIES.
  const isAgentAnalyticsEnabled =
    isFacilityEnrolledInAgentAnalytics(currentUser?.facility_id) &&
    isFacilityEnrolledInAgentAnalytics(requestFacilityId);

  // Source metrics are only shown (and their APIs only called) for facilities
  // enrolled in REACT_APP_CLIENT_APP_FILTER_FACILITIES, keyed off the facility
  // whose data is on screen — the superadmin-adjustable filter for superadmins,
  // the admin's own facility otherwise.
  const isSourceMetricsEnabled =
    isFacilityEnrolledInClientAppFilter(requestFacilityId);

  const [agentAnalytics, setAgentAnalytics] = useState(null);
  const [agentAnalyticsLoading, setAgentAnalyticsLoading] = useState(true);

  useEffect(() => {
    if (!isAgentAnalyticsEnabled) {
      setAgentAnalyticsLoading(false);
      return;
    }
    let cancelled = false;
    const fetchAgentAnalytics = async () => {
      try {
        setAgentAnalyticsLoading(true);
        const { start_date, end_date } = resolveAnalyticsDateRange({
          startValue: selectedStartDateFilter,
          endValue: selectedEndDateFilter,
        });
        const res = await DashboardMetricsService.getAgentAnalytics({
          facility_id: requestFacilityId,
          start_date,
          end_date,
        });
        if (!cancelled) setAgentAnalytics(res.data);
      } catch (error) {
        console.error("Failed to fetch agent analytics", error);
        if (!cancelled) setAgentAnalytics(null);
      } finally {
        if (!cancelled) setAgentAnalyticsLoading(false);
      }
    };
    fetchAgentAnalytics();
    return () => {
      cancelled = true;
    };
  }, [selectedStartDateFilter, selectedEndDateFilter, requestFacilityId, isAgentAnalyticsEnabled]);

  // Query Categories — independent of the Agent Analytics enrollment gate above;
  // every facility gets this since categorization runs for every message already.
  // `categorySource` re-triggers the fetch (scoped server-side), same shape as
  // the facility/date filters.
  const [categoryAnalytics, setCategoryAnalytics] = useState(null);
  const [categoryAnalyticsLoading, setCategoryAnalyticsLoading] = useState(true);
  const [categorySource, setCategorySource] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCategoryAnalytics = async () => {
      try {
        setCategoryAnalyticsLoading(true);
        const { start_date, end_date } = resolveAnalyticsDateRange({
          startValue: selectedStartDateFilter,
          endValue: selectedEndDateFilter,
        });
        const res = await DashboardMetricsService.getCategoryAnalytics({
          facility_id: requestFacilityId,
          start_date,
          end_date,
          source: categorySource,
        });
        if (!cancelled) setCategoryAnalytics(res.data);
      } catch (error) {
        console.error("Failed to fetch category analytics", error);
        if (!cancelled) setCategoryAnalytics(null);
      } finally {
        if (!cancelled) setCategoryAnalyticsLoading(false);
      }
    };
    fetchCategoryAnalytics();
    return () => {
      cancelled = true;
    };
  }, [selectedStartDateFilter, selectedEndDateFilter, requestFacilityId, categorySource]);

  return (
    <div className="w-full space-y-6">

      <GenericAnalytics tokenUsage={tokenUsage} />

      {/* ======================================================
         SOURCE METRICS (per client app)
         ====================================================== */}
      {isSourceMetricsEnabled && (
        <Accordion title={`Platform & ${getDesktopBuddyLabel(requestFacilityId)} Usage`}>
          <div className="grid grid-cols-1">
            <SourceMetrics buddyLabel={getDesktopBuddyLabel(requestFacilityId)} />
          </div>
        </Accordion>
      )}

      {/* ======================================================
         AGENT ANALYTICS
         ====================================================== */}
      {isAgentAnalyticsEnabled && (
        <Accordion title="Agent Analytics">
          <div className="grid grid-cols-1">
            <AgentAnalytics agentAnalytics={agentAnalytics} loading={agentAnalyticsLoading} />
          </div>
        </Accordion>
      )}

      {/* ======================================================
         QUERY CATEGORIES
         ====================================================== */}
      <Accordion title="Query Categories">
        <div className="grid grid-cols-1">
          <QueryCategories
            categoryAnalytics={categoryAnalytics}
            loading={categoryAnalyticsLoading}
            source={categorySource}
            onSourceChange={setCategorySource}
          />
        </div>
      </Accordion>

      {/* ======================================================
         TOKEN USAGE INSIGHTS
         ====================================================== */}
      <Accordion title="Token Usage Analytics">
        <div className="grid grid-cols-1">
          <TokenUsageAnalytics tokenUsage={tokenUsage} loading={tokenUsageLoading} />
        </div>
      </Accordion>

      {/* ======================================================
         USER ANALYTICS
         ====================================================== */}
      <Accordion title="User Analytics">
        <div className="grid grid-cols-1">
          <UserAnalytics />
        </div>
      </Accordion>

      {/* ======================================================
         Platform INSIGHTS
         ====================================================== */}
      <Accordion title="Platform Insights">
        <div className="grid grid-cols-1">
          <Card title="">
            <PlatformUsage />
          </Card>
        </div>
      </Accordion>


      {/* ======================================================
         PROJECTS / COLLECTIONS INSIGHTS
         ====================================================== */}
      <Accordion title="Projects / Collections Insights">
        <div className="grid grid-cols-1">
          <ProjectAnalytics />
        </div>
      </Accordion>

      {/* ======================================================
         WORKSPACE INSIGHTS
         ====================================================== */}
      <Accordion title="Workspace Insights">
        <div className="grid grid-cols-1">
          <WorkspaceAnalytics />
        </div>
      </Accordion>

      {/* ======================================================
         FILE GENERATION INSIGHTS
         ====================================================== */}
      <Accordion title="File Generation Analytics">
        <div className="grid grid-cols-1">
          <FileGenerationAnalytics />
        </div>
      </Accordion>

      {/* ======================================================
         FILE UPLOADING INSIGHTS
         ====================================================== */}
      <Accordion title="File Upload Insights">
        <div className="grid grid-cols-1">
          <FileAnalytics />

          {/* <Card title="File Type Table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">File Type</th>
                  <th className="text-right py-2">Count</th>
                </tr>
              </thead>
              <tbody>
                {fileTypeTable.map((row) => (
                  <tr key={row.type} className="border-b last:border-none">
                    <td className="py-2">{row.type}</td>
                    <td className="py-2 text-right">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card> */}
        </div>
      </Accordion>



    </div>
  );
}
